import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import {
  StatusRippleHeader,
  Text,
  StatusColorIndicatorOption,
} from '@adsk/bim360-matrix-react-components';
import { connect } from '../../../../redux/connect';
import * as ReportsThunk from '../../../../redux/thunk/reportsThunk';
import { getIsCreatingPdfReport, getReportContainerId } from '../../../../redux/selectors';
import {
  issueAttributesType,
  statusOptionsArrayType,
} from '../../../../constants/proptype-validation';
import { statusColorMapping } from '../../../../constants/status-types';
import { actions as pushpinActions } from '../../../../constants/pushpin-visibility';
import {
  ISSUE_ATTRIBUTE_NAMES,
  ISSUE_BE_ATTRIBUTE_NAMES,
} from '../../../../constants/issue-attribute-names';
import { getRfiDetailPayload } from '../../../../utils/reportHelpers';
import { isAttributePermitted } from '../../../../utils/issues-helpers';
import exportIcon from '../../../../assets/images/icons/icon-export-thin.svg';
import { toReportFileNameDate } from '../../../../utils/date-helpers';
import { trimFileName } from '../../../../utils/notificationHelpers';
import TryAgainErrorNotification from '../../../TryAgainErrorNotification';
import RfiCustomIdentifierInlineEdit from './components/RfiCustomIdentifierInlineEdit';

export class PureIssuePreviewHeader extends PureComponent {
  constructor(props) {
    super(props);
    this.statusOptionRenderer = this.statusOptionRenderer.bind(this);
    this.handleActionOptionSelect = this.handleActionOptionSelect.bind(this);
    this.createReport = this.createReport.bind(this);
  }

  getHeaderTitle() {
    const { issue } = this.props;
    const isCustomIdentifierEditable = isAttributePermitted(
      issue,
      ISSUE_BE_ATTRIBUTE_NAMES[ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER]
    );

    if (isCustomIdentifierEditable) {
      return <RfiCustomIdentifierInlineEdit issue={issue} />;
    }

    return issue.customIdentifier ? (
      `#${issue.customIdentifier}`
    ) : (
      <div className="IssuePreviewHeader__title-unspecified">
        {`#${T.translate('unspecifiedValue')}`}
      </div>
    );
  }

  getExportButtonConfig() {
    const { isCreatingPdfReport, isPDFReportEnabled } = this.props;
    const exportTooltipText = T.translate('export.exportSinglePDFButtonTooltip');
    let exportButtonConfig = null;

    if (isPDFReportEnabled) {
      exportButtonConfig = {
        icon: exportIcon,
        onClick: this.createReport,
        tooltipText: exportTooltipText,
      };

      // Disable export button when exporting in progress
      if (isCreatingPdfReport) {
        exportButtonConfig.disabled = true;
        exportButtonConfig.tooltipText = null;
      }
    }

    return exportButtonConfig;
  }

  statusOptionRenderer(value, label) {
    const { issueStatusOptions } = this.props;
    const option = issueStatusOptions.find((s) => s.value === value);
    return (
      <StatusColorIndicatorOption
        statusColor={statusColorMapping[option.value]}
        label={label}
        subText={option.description}
      />
    );
  }

  createReport() {
    const { issue, createReport } = this.props;
    const { title, customIdentifier } = issue;

    const payload = getRfiDetailPayload(issue);
    const currentDate = toReportFileNameDate(new Date());
    const issueId = customIdentifier || T.translate('unspecifiedValue');
    const fileName = `${issueId}_${title}_${currentDate}.pdf`;
    const trimmedFileName = trimFileName(fileName, 35);
    const textExportFailed = T.translate('notifications.failedToDownload', {
      fileName: trimmedFileName,
    });
    const failureMessage = (
      <TryAgainErrorNotification
        message={textExportFailed}
        tryAgainAction={() => this.createReport()}
      />
    );
    const asyncMessage = (
      <Text hasHTML>
        {T.translate('notifications.preparingToDownload', { fileName: trimmedFileName })}
      </Text>
    );

    createReport(payload, issue, fileName, asyncMessage, failureMessage);
  }

  handleActionOptionSelect(actionKey) {
    const { onPushpinVisibilityChange } = this.props;
    switch (actionKey) {
      case PureIssuePreviewHeader.availableActions.EXPORT: {
        this.createReport();
        break;
      }
      case PureIssuePreviewHeader.availableActions.HIDE:
      case PureIssuePreviewHeader.availableActions.SHOW:
        onPushpinVisibilityChange();
        break;
      default:
        break;
    }
  }

  render() {
    const {
      changeToStatus,
      issueStatusOptions,
      issue,
      enableStatusChange,
      onStatusChange,
    } = this.props;
    const currentStatus = changeToStatus ? changeToStatus.value : issue.status;
    const currentStatusOption = issueStatusOptions.find((status) => status.value === currentStatus);

    const exportButtonConfig = this.getExportButtonConfig();

    return (
      <div className="IssuePreviewHeader" data-testid="IssuePreviewHeader">
        <StatusRippleHeader
          onStatusChange={onStatusChange}
          optionRenderer={this.statusOptionRenderer}
          enableStatusChange={enableStatusChange}
          superTitle={T.translate('rfis.label_capitalized')}
          title={this.getHeaderTitle()}
          changeToStatus={changeToStatus}
          currentStatusOption={currentStatusOption}
          currentStatusName={currentStatusOption.label}
          currentStatusColorClassName={currentStatusOption.colorClassName}
          statusOptions={issueStatusOptions}
          extraActionButton={exportButtonConfig}
        />
      </div>
    );
  }
}

PureIssuePreviewHeader.availableActions = {
  EXPORT: 'EXPORT',
  HIDE: pushpinActions.HIDE,
  SHOW: pushpinActions.SHOW,
};

PureIssuePreviewHeader.displayName = 'IssuePreviewHeader';
PureIssuePreviewHeader.defaultProps = {
  issue: undefined,
  changeToStatus: undefined,
  isCreatingPdfReport: undefined,
  onPushpinVisibilityChange: undefined,
};
PureIssuePreviewHeader.propTypes = {
  onStatusChange: PropTypes.func.isRequired,
  issue: issueAttributesType,
  issueStatusOptions: statusOptionsArrayType.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  changeToStatus: PropTypes.object,
  enableStatusChange: PropTypes.bool.isRequired,
  isPDFReportEnabled: PropTypes.bool.isRequired,
  isCreatingPdfReport: PropTypes.bool,
  createReport: PropTypes.func.isRequired,
  onPushpinVisibilityChange: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isPDFReportEnabled: !!getReportContainerId(state),
  isCreatingPdfReport: getIsCreatingPdfReport(state),
});

const mapDispatchToProps = {
  createReport: ReportsThunk.createReportSync,
};

export default connect(mapStateToProps, mapDispatchToProps)(PureIssuePreviewHeader);
